import React from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import localStorage from 'local-storage'
import {
  LogoutOutlined,
  Menu as MenuIcon,
  GroupOutlined,
} from '@mui/icons-material'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Package } from 'tabler-icons-react'
import largeLogo from '../../assets/images/rahatUp.png'
import smallLogo from '../../assets/images/rahatsistem-logo.png'
import { logout } from '../../api/auth/logout'

const menuItems = [
  {
    header: true,
    text: 'Yönetim',
  },
  {
    text: 'Kullanıcılar',
    icon: <GroupOutlined fontSize="small" />,
    path: '/admin/users',
  },
  {
    text: 'Abonelik Planları',
    icon: <Package size={20} />,
    path: '/admin/packages',
  },
  {
    header: true,
    text: 'Diğer',
  },
  {
    text: 'Çıkış',
    icon: <LogoutOutlined fontSize="small" />,
    action: logout,
    path: '/login',
  },
]

const AdminSidebar = ({ status, toggleSidebar }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const handleItemClick = (item) => {
    if (item.action) {
      item.action()
    }
    if (item.path) {
      navigate(item.path)
    }
  }

  const drawerWidth = status ? 260 : 72

  const renderMenuItem = (item, index) => {
    const isActive = location.pathname === item.path
    const isExpanded = status

    if (item.header) {
      return (
        <AnimatePresence key={`header-${index}`}>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 3,
                  pt: index === 0 ? 0 : 3,
                  pb: 1,
                  color: alpha('#fff', 0.5),
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                }}
              >
                {item.text}
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      )
    }

    const listItemStyles = {
      mx: 1.5,
      mb: 0.5,
      borderRadius: 2,
      minHeight: 44,
      position: 'relative',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: isActive
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(
            theme.palette.primary.main,
            0.05
          )})`
        : 'transparent',
      border: `1px solid ${isActive ? alpha(theme.palette.primary.main, 0.3) : 'transparent'}`,
      '&:hover': {
        background: isActive
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(
              theme.palette.primary.main,
              0.1
            )})`
          : alpha(theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        transform: 'translateX(4px)',
      },
      '& .MuiListItemIcon-root': {
        minWidth: 36,
        color: isActive ? theme.palette.primary.main : alpha('#fff', 0.7),
        transition: 'color 0.2s ease',
      },
      '& .MuiListItemText-primary': {
        color: isActive ? theme.palette.primary.main : alpha('#fff', 0.9),
        fontWeight: isActive ? 600 : 500,
        fontSize: '0.875rem',
      },
    }

    return (
      <Tooltip
        key={`menu-${index}`}
        title={!isExpanded ? item.text : ''}
        placement="right"
        arrow
      >
        <ListItem
          button
          onClick={item.action ? () => handleItemClick(item) : undefined}
          component={item.action ? undefined : Link}
          to={item.action ? undefined : item.path}
          sx={listItemStyles}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {isExpanded && (
            <ListItemText
              primary={item.text}
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            />
          )}
        </ListItem>
      </Tooltip>
    )
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.background.default,
            0.98
          )}, ${alpha(theme.palette.background.paper, 0.98)})`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: status ? 'space-between' : 'center',
            minHeight: 64,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={status ? 'expanded-logo' : 'collapsed-logo'}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                component="img"
                src={status ? largeLogo : smallLogo}
                alt="RahatUp"
                sx={{ height: 32, filter: 'brightness(0) invert(1)' }}
              />
            </motion.div>
          </AnimatePresence>

          {status && (
            <IconButton
              onClick={() => {
                toggleSidebar(!status)
                localStorage.set('admin_sidebar', `${!status}`)
              }}
              sx={{
                color: alpha('#fff', 0.7),
                '&:hover': {
                  color: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Yönetici rozeti */}
        {status && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: theme.palette.primary.main,
                }}
              >
                YÖNETİCİ PANELİ
              </Typography>
            </Box>
          </Box>
        )}

        {/* Menu */}
        <List
          sx={{
            flex: 1,
            pt: 2,
            pb: 2,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.primary.main, 0.3),
              borderRadius: 2,
              '&:hover': { background: alpha(theme.palette.primary.main, 0.5) },
            },
          }}
        >
          {menuItems.map(renderMenuItem)}
        </List>

        {/* Kapalıyken açma butonu */}
        {!status && (
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              justifyContent: 'center',
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <IconButton
              onClick={() => {
                toggleSidebar(!status)
                localStorage.set('admin_sidebar', `${!status}`)
              }}
              sx={{
                color: alpha('#fff', 0.7),
                '&:hover': {
                  color: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

export default AdminSidebar
